import { z } from 'zod';

export namespace RemirrorSchemas {
  // NodeType enum as a Zod union
  const NodeTypeSchema = z.enum([
    // survey
    'price-link',
    'piped',
    'category',
    'template-value',
    'target',
    'competitor',
    'tpp',
    'view-stimulus',
    'view-tpp',
    'view-tpp-rt',
    'find-and-replace',
    // end survey
    'bulletList',
    'doc',
    'listItem',
    'paragraph',
    'text',
    'image',
  ]);

  // Marks
  const BoldMarkSchema = z.object({ type: z.literal('bold') });
  const ItalicMarkSchema = z.object({ type: z.literal('italic') });
  const UnderLinkMarkSchema = z.object({ type: z.literal('underline') });
  const LinkMarkSchema = z.object({
    type: z.literal('link'),
    attrs: z.object({ href: z.string() }),
  });
  const TextColorMarkSchema = z.object({
    type: z.literal('textColor'),
    attrs: z.object({ color: z.string() }),
  });
  const TextMarkSchema = z.union([
    BoldMarkSchema,
    ItalicMarkSchema,
    UnderLinkMarkSchema,
    LinkMarkSchema,
    TextColorMarkSchema,
  ]);

  // TextNode
  const TextNodeSchema = z.object({
    type: z.literal('text'),
    text: z.string(),
    marks: z.array(TextMarkSchema).optional(),
  });

  // ParagraphNode
  const ParagraphNodeSchema = z.object({
    type: z.literal('paragraph'),
    content: z.array(TextNodeSchema).optional(),
  });

  // ListItemNode
  const ListItemNodeSchema = z.object({
    type: z.literal('listItem'),
    attrs: z.object({
      closed: z.boolean(),
      nested: z.boolean(),
    }),
    content: z.array(ParagraphNodeSchema).optional(),
  });

  // BulletListNode
  const BulletListNodeSchema = z.object({
    type: z.literal('bulletList'),
    content: z.array(ListItemNodeSchema).optional(),
  });

  // ImageExtensionAttributes
  const ImageExtensionAttributesSchema = z.object({
    align: z.enum(['center', 'end', 'justify', 'left', 'match-parent', 'right', 'start']).optional(),
    alt: z.string().optional(),
    height: z.union([z.string(), z.number()]).optional(),
    width: z.union([z.string(), z.number()]).optional(),
    rotate: z.string().optional(),
    src: z.string(),
    title: z.string().optional(),
    fileName: z.string().optional(),
  });

  // ImageNode
  const ImageNodeSchema = z.object({
    type: z.literal('image'),
    attrs: ImageExtensionAttributesSchema.optional(),
  });

  // RootNodeContent (ParagraphNode)
  const RootNodeContentSchema = ParagraphNodeSchema;

  // RootNode
  const RootNodeSchema = z.object({
    type: z.literal('doc'),
    content: z.array(RootNodeContentSchema),
  });

  // Export all schemas as needed
  export const RemirrorSchema = {
    NodeType: NodeTypeSchema,
    BoldMark: BoldMarkSchema,
    ItalicMark: ItalicMarkSchema,
    UnderLinkMark: UnderLinkMarkSchema,
    LinkMark: LinkMarkSchema,
    TextColorMark: TextColorMarkSchema,
    TextMark: TextMarkSchema,
    TextNode: TextNodeSchema,
    ParagraphNode: ParagraphNodeSchema,
    ListItemNode: ListItemNodeSchema,
    BulletListNode: BulletListNodeSchema,
    ImageExtensionAttributes: ImageExtensionAttributesSchema,
    ImageNode: ImageNodeSchema,
    RootNodeContent: RootNodeContentSchema,
    RootNode: RootNodeSchema,
  };
}
